import React from 'react';
import { FieldTypeEnum } from '../../FieldBank/_types';
import { BaseTextInput, BaseNumberInput, BaseBooleanInput, BaseDateInput, BaseTimeInput } from './index';

export interface DirectFieldInputProps {
  fieldType: FieldTypeEnum;
  value: any;
  onChange: (value: any) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  hideLabel?: boolean;
  // Additional props for specific field types
  maxLength?: number;
  min?: number;
  max?: number;
  showTimezone?: boolean;
}

const DirectFieldInput: React.FC<DirectFieldInputProps> = ({
  fieldType,
  value,
  onChange,
  label,
  placeholder,
  required = false,
  disabled = false,
  error,
  helperText,
  size = 'small',
  fullWidth = true,
  hideLabel = false,
  maxLength,
  min,
  max,
  showTimezone = true,
}) => {
  const commonProps = {
    value,
    onChange,
    label,
    required,
    disabled,
    error,
    helperText,
    size,
    fullWidth,
  };

  switch (fieldType) {
    case 'Text':
    case 'Lookup':
      return (
        <BaseTextInput
          {...commonProps}
          placeholder={placeholder || 'Enter default text value'}
          maxLength={maxLength}
        />
      );

    case 'Number':
      return (
        <BaseNumberInput
          {...commonProps}
          placeholder={placeholder || 'Enter default number'}
          min={min}
          max={max}
        />
      );

    case 'Boolean':
      return (
        <BaseBooleanInput
          {...commonProps}
          label={hideLabel ? '' : label || 'Default value'}
        />
      );

    case 'Date':
      return (
        <BaseDateInput
          {...commonProps}
          label={hideLabel ? '' : label || 'Default date value'}
        />
      );

    case 'Time':
      return (
        <BaseTimeInput
          {...commonProps}
          label={hideLabel ? '' : label || 'Default time value'}
          showTimezone={showTimezone}
        />
      );

    case 'SingleSelect':
    case 'MultiSelect':
      // SingleSelect/MultiSelect will be handled separately due to its complexity
      // For now, fall back to text input
      return (
        <BaseTextInput
          {...commonProps}
          placeholder={placeholder || 'Select default option'}
          disabled={true}
          helperText='SingleSelect inputs require special handling'
        />
      );

    case 'Attachment':
    case 'BodyImage':
    case 'VehicleImage':
      return null;

    case 'Narrative':
      return (
        <BaseTextInput
          {...commonProps}
          placeholder={placeholder || 'Enter default narrative'}
          multiline={true}
          minRows={3}
        />
      );

    default:
      console.warn('Unknown field type:', fieldType, 'defaulting to Text input');
      return (
        <BaseTextInput
          {...commonProps}
          placeholder={placeholder || 'Enter default value'}
        />
      );
  }
};

export default DirectFieldInput;
